<template>
  <div class="max-w-3xl w-full mx-auto space-y-6">
    <h1 class="text-2xl font-bold text-gray-800">Choose an admin action</h1>
    <div v-if="hasValidToken" class="grid md:grid-cols-2 gap-6">
      
      <div v-if="canUpload" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 6a2 2 0 012-2h10a2 2 0 012 2v1.382l3.447-1.724A1 1 0 0121 6.553v6.894a1 1 0 01-1.553.895L16 12.618V14a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
          </svg>
          <h2 class="text-lg font-semibold">Clip Source Upload</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Upload a source video for a ceremony.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/upload"
          @click.native.capture="trackCardClick('clip_upload')"
        >
          Go to Clip Upload
        </router-link>
      </div>

      <div v-if="canSourceInputs" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2h7V4H4zm9 0v8h7a2 2 0 002-2V6a2 2 0 00-2-2h-7zM2 16a2 2 0 012-2h16a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2z"/>
          </svg>
          <h2 class="text-lg font-semibold">Source Inputs</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Review IVS ingest, external URLs, schedules, and stuck source jobs.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/source-inputs"
          @click.native.capture="trackCardClick('source_inputs')"
        >
          Open Source Inputs
        </router-link>
      </div>

      <div v-if="canSnapshotUpload" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 5a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V7a2 2 0 00-2-2H4zm3 3l3 4 2-2 4 5H6l1-7z"/>
          </svg>
          <h2 class="text-lg font-semibold">Snapshot Bulk Upload</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Upload, shoot, and review snapshot photos for a ceremony.</p>
        <div class="mt-auto flex flex-wrap gap-2">
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/snapshot-bulk"
            @click.native.capture="trackCardClick('snapshot_bulk_upload')"
          >
            Bulk Upload
          </router-link>
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/snapshot-bulk?mode=camera"
            @click.native.capture="trackCardClick('snapshot_camera_mode')"
          >
            Camera Mode
          </router-link>
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/snapshot-problems"
            @click.native.capture="trackCardClick('snapshot_problems')"
          >
            Review Problems
          </router-link>
          <router-link
            v-if="canConfigureSnapshot"
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/snapshot-config"
            @click.native.capture="trackCardClick('snapshot_config')"
          >
            Configure Snapshot
          </router-link>
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/snapshot-operations"
            @click.native.capture="trackCardClick('snapshot_operations')"
          >
            Operations
          </router-link>
        </div>
      </div>

      <div v-if="canViewOrders" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 9a1 1 0 011-1h2v10H6a1 1 0 01-1-1V9zm6-4a1 1 0 011-1h2v14h-2a1 1 0 01-1-1V5zm6 7a1 1 0 011-1h2v7h-2a1 1 0 01-1-1v-5z"/>
          </svg>
          <h2 class="text-lg font-semibold">View Order Analytics</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">View order analytics and insights for retail products.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/order-analytics"
          @click.native.capture="trackCardClick('orders_analytics')"
        >
          Open Order Analytics
        </router-link>
      </div>
      <div v-if="canEditShare" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 2L11 13"/>
            <path d="M22 2L15 22l-4-9-9-4z"/>
          </svg>

          <h2 class="text-lg font-semibold">Share Message Presets</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Configure default share text per platform for each university.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/share-templates"
          @click.native.capture="trackCardClick('share_presets')"
        >
          Open Share Presets
        </router-link>
      </div>
      <div v-if="canEditUploadNotifications" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <h2 class="text-lg font-semibold">University Upload Notifications</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Manage university-level recipients for upload complete and upload processed notifications.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/upload-notifications"
          @click.native.capture="trackCardClick('upload_notifications')"
        >
          Open University Upload Notifications
        </router-link>
      </div>

      <div v-if="canViewAnalytics" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2H5zm3 12a1 1 0 011-1h2v4H9a1 1 0 01-1-1v-2zm5-4a1 1 0 011-1h2v8h-2a1 1 0 01-1-1V11zM7 11a1 1 0 011-1h2v6H8a1 1 0 01-1-1v-4z"/>
          </svg>
          <h2 class="text-lg font-semibold">Engagement Analytics</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">View engagement metrics per university.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/analytics"
          @click.native.capture="trackCardClick('engagement_analytics')"
        >
          Open Analytics Dashboard
        </router-link>
      </div>
      <div v-if="canExploreClips" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 3a7 7 0 105.292 11.708l4 4a1 1 0 001.416-1.416l-4-4A7 7 0 0010 3zm0 2a5 5 0 110 10 5 5 0 010-10z"/>
          </svg>
          <h2 class="text-lg font-semibold">Clip Explorer</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Search and browse clips by university and ceremony.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/clips"
          @click.native.capture="trackCardClick('clips')"
        >
          Open Clip Explorer
        </router-link>
      </div>
      <div v-if="canManageUsers" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 11a4 4 0 10-8 0 4 4 0 008 0zm-4 6c-4.418 0-8 1.79-8 4v1h16v-1c0-2.21-3.582-4-8-4zm7-9V5h-3a1 1 0 110-2h3V0a1 1 0 112 0v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0z"/>
          </svg>
          <h2 class="text-lg font-semibold">User Management</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Create users, update roles and university access, and reset passwords.</p>
        <router-link
          class="mt-auto inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          to="/admin/users"
          @click.native.capture="trackCardClick('users')"
        >
          Manage Users
        </router-link>
      </div>
      <div v-if="canUseImports" class="border rounded-lg p-6 bg-white shadow-sm flex flex-col">
        <div class="flex items-center mb-2">
          <svg class="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v3H3V4zm0 5h18v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9zm3 2v2h4v-2H6zm0 4v2h8v-2H6z"/>
          </svg>
          <h2 class="text-lg font-semibold">Roster Imports</h2>
        </div>
        <p class="text-sm text-gray-600 mb-4">Import graduate lists per ceremony and generate staged data.</p>
        <div class="mt-auto flex gap-2">
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/imports"
            @click.native.capture="trackCardClick('imports')"
          >
            View Imports
          </router-link>
          <router-link
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            to="/admin/imports/new"
            @click.native.capture="trackCardClick('imports_new')"
          >
            New Import
          </router-link>
        </div>
      </div>
    </div>
    <div v-if="hasValidToken && !hasAnyAccess" class="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
      You do not have access to any admin tools.
    </div>
  </div>
</template>

<script>
import { trackAdminEvent } from '../../admin/activity.js'
import { getAccessContext, hasToolAccess } from '../../admin/access.js'

export default {
  name: 'ChooseAction',
  created() {
    trackAdminEvent('admin_chooser_opened', {
      toolKey: 'admin_choose',
    })
  },
  computed: {
    accessContext() {
      return getAccessContext()
    },
    hasValidToken() {
      return this.accessContext.valid
    },
    canUpload() {
      return hasToolAccess('upload', this.accessContext)
    },
    canSourceInputs() {
      return hasToolAccess('sourceInputs', this.accessContext)
    },
    canUseImports() {
      return hasToolAccess('imports', this.accessContext)
    },
    canSnapshotUpload() {
      return hasToolAccess('snapshotBulk', this.accessContext)
    },
    canConfigureSnapshot() {
      return hasToolAccess('snapshotConfig', this.accessContext)
    },
    canEditShare() {
      return hasToolAccess('shareTemplates', this.accessContext)
    },
    canEditUploadNotifications() {
      return hasToolAccess('uploadNotifications', this.accessContext)
    },
    canViewOrders() {
      return hasToolAccess('ordersAnalytics', this.accessContext)
    },
    canViewAnalytics() {
      return hasToolAccess('analytics', this.accessContext)
    },
    canExploreClips() {
      return hasToolAccess('clipsExplorer', this.accessContext)
    },
    canManageUsers() {
      return hasToolAccess('userManagement', this.accessContext)
    },
    hasAnyAccess() {
      return this.canUpload ||
        this.canSourceInputs ||
        this.canSnapshotUpload ||
        this.canConfigureSnapshot ||
        this.canViewOrders ||
        this.canEditShare ||
        this.canEditUploadNotifications ||
        this.canViewAnalytics ||
        this.canUseImports ||
        this.canExploreClips ||
        this.canManageUsers
    }
  },
  methods: {
    trackCardClick(toolKey) {
      trackAdminEvent('admin_card_clicked', {
        toolKey,
      })
    }
  }
}
</script>

<style scoped>
</style>
